import { useTranslation } from 'react-i18next';
import './AmazonConnection.scss';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import WhiteGreenWrapper from '../../../wrappers/WhiteGreenWrapper';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  getAmazonDataImportStatus,
  getAmazonConnectCredentials,
  postClientAmazonConnect,
  postTriggerAmazonDataImport,
  patchUser,
  getUserCompanies,
} from '../../../core/api';
import {
  setAmazonDataImportStatus,
  setHasNewAmazonCredentials,
} from '../../../core/store/amazon/amazonSlice';
import classNames from 'classnames';
import { Loader } from '../../../components/Loader/Loader';
import { sleep } from '../../../core/helpers';
import { ReactComponent as ShieldIcon } from '../../../icons/shield.svg';
import { ReactComponent as AmazonLogo } from '../../../icons/amazon.svg';
import AmazonConnectImage from '../../../icons/amazon-connect.svg';
import AmazonImportedImage from '../../../icons/amazon-imported.svg';
import AmazonConnectImageError from '../../../icons/amazon-connect-error.svg';
import { useHistory, useLocation } from 'react-router-dom';
import Progress from '../../../components/Progress/Progress';
import { AmazonDataImportStatusModel } from '../../../core/types';
import MiniLoader from '../../../components/MiniLoader/MiniLoader';
import AmazonConnectionConsultation from './AmazonConnectionConsultation';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  setActiveCompany,
  setUserCompanies,
} from '../../../core/store/user/userSlice';
import Button from '../../../components/Button';

export interface AmazonConnectionFormValues {
  seller_id: string;
  mws_auth_token: string;
}

export interface AmazonConnectionFormValues {
  seller_id: string;
  mws_auth_token: string;
}

export default function AmazonConnection() {
  const { vs2931NotAmazonSellerBtn } = useFlags();
  const [isAmazonSeller, setIsAmazonSeller] = useState(true);
  const history = useHistory();
  const user = useAppSelector((s) => s.user.user);
  const hasNewCredentials = useAppSelector((s) => s.amazon.hasNewCredentials);
  const controller = useRef(new AbortController());
  const locaton = useLocation();
  const params = new URLSearchParams(locaton.search);
  const state = params.get('state');
  const spapi_oauth_code = params.get('spapi_oauth_code');
  const selling_partner_id = params.get('selling_partner_id');

  const amazonDataImportStatus = useAppSelector(
    (s) => s.amazon.dataImportStatus
  );
  const { t, i18n } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(
    amazonDataImportStatus === 'in-process'
  );
  const [isDataImported, setIsDataImported] = useState(
    amazonDataImportStatus === 'loaded'
  );
  const [isError, setIsError] = useState(false);
  const [progressData, setProgressData] =
    useState<AmazonDataImportStatusModel>();

  const email = useAppSelector((state) => state.user.user?.Email);

  const dispatch = useAppDispatch();

  const onCheckNowClick = () => {
    dispatch(setAmazonDataImportStatus('loaded'));
  };

  const onFinishDataImport = useCallback(() => {
    setIsProcessing(false);
    setIsDataImported(true);
  }, []);

  useEffect(() => {
    return () => {
      // eslint-disable-next-line
      controller.current.abort();
    };
  }, []);

  const updateImportData = (data: AmazonDataImportStatusModel) =>
    setProgressData(data);

  useEffect(() => {
    if (hasNewCredentials) {
      setIsLoading(true);
      getAmazonDataImportStatus()
        .then(async (res) => {
          updateImportData(res);
          setIsProcessing(true);
          setIsLoading(false);
          if (res.status === 'not-started') {
            await postTriggerAmazonDataImport();
          }
          pollAmazonStatus(
            onFinishDataImport,
            updateImportData,
            controller.current
          );
        })
        .catch(console.error)
        .finally(() => {
          setIsLoading(false);
        });
    }
    // eslint-disable-next-line
  }, [onFinishDataImport]);

  useEffect(() => {
    if (
      state &&
      state.replace('setup-check', '') === user?.Id &&
      spapi_oauth_code &&
      selling_partner_id
    ) {
      (async () => {
        try {
          setIsLoading(true);
          await postClientAmazonConnect({
            seller_id: selling_partner_id,
            oauth_code: spapi_oauth_code,
          });
          const credsRes = await getAmazonConnectCredentials();
          history.replace('/');
          if (!!credsRes.length) {
            dispatch(setHasNewAmazonCredentials(true));
            setIsLoading(false);

            try {
              setIsProcessing(true);
              await postTriggerAmazonDataImport();
              pollAmazonStatus(
                onFinishDataImport,
                updateImportData,
                controller.current
              );
              const companies = await getUserCompanies();
              dispatch(setUserCompanies(companies));
              dispatch(setActiveCompany(companies[0]));
            } catch {
              console.error('Import error, please try again');
            }
          } else {
            throw new Error('not connected');
          }
        } catch {
          setIsError(true);
          setIsLoading(false);
          history.replace('/');
        }
      })();
    }
    // eslint-disable-next-line
  }, [state, spapi_oauth_code, selling_partner_id]);

  const progressPercent = getProgressPercent(
    progressData?.total,
    progressData?.processed
  );

  const renderRightColumnContent = () => {
    if (isDataImported) {
      return (
        <div className='d-flex flex-column align-items-center justify-content-center pt-1'>
          <img
            src={AmazonImportedImage}
            alt='AmazonImportedImage'
            className='AmazonConnection__image mx-auto'
          />
          <div className='Title mt-4 fw-900 text-center'>{t('Imported')}</div>
          <div className='text-center mb-4'>
            {t('We also have sent your results to *email*').replace(
              '*email*',
              email || ''
            )}
          </div>
          <Progress
            max={1}
            value={1}
            className='Progress--AmazonConnection'
            getProgressBarColor={() => {
              return '#FFE609';
            }}
          />
        </div>
      );
    }
    if (isProcessing) {
      return (
        <div>
          <img
            src={AmazonConnectImage}
            alt='AmazonConnectImage'
            className='AmazonConnection__image mx-auto'
          />
          <div
            className='Title mt-3 pt-3 fw-900 text-center mb-2'
            data-testid='time'
          >
            {isNaN(progressPercent) || progressPercent < 0.5 ? (
              t('Starting processing')
            ) : (
              <>
                {Math.round(progressPercent)}% {t('processed')}
              </>
            )}
          </div>
          <div className='text-center mb-4'>
            {t(
              'It might take a few minutes. We will notify to *email* once it is done'
            ).replace('*email*', email || '')}
          </div>
          <div className='d-flex justify-content-center'>
            <Progress
              max={progressData?.total || 100}
              value={
                isNaN(progressPercent) || progressPercent < 0.5
                  ? 0
                  : progressData?.processed || 0
              }
              className='Progress--AmazonConnection'
              getProgressBarColor={() => {
                return '#FFE609';
              }}
            />
          </div>
        </div>
      );
    }

    return (
      <div>
        <div className='text-center pt-1'>
          {isError ? (
            <img
              src={AmazonConnectImageError}
              alt='AmazonConnectImageError'
              className='AmazonConnection__image mx-auto'
            />
          ) : (
            <img
              src={AmazonConnectImage}
              alt='AmazonConnectImage'
              className='AmazonConnection__image mx-auto'
            />
          )}
        </div>
        <div className='text-center mt-3 pt-3 Title text-white fw-900 mb-2'>
          {t('Approx. 15 min')}
        </div>
        <div className='text-center mb-4'>
          {t('We will notify to *email* once it is done').replace(
            '*email*',
            email || ''
          )}
        </div>
      </div>
    );
  };

  const connectedBadge = (
    <div className='Text AmazonConnection__authorizeBtn AmazonConnection__authorizeBtn--connected d-flex p-0 align-items-stretch text-decoration-none w-100 overflow-hidden mb-5'>
      <div
        className={`AmazonConnection__authorizeIcon d-flex align-items-center justify-content-center`}
      >
        <AmazonLogo />
      </div>
      <div
        className='d-flex align-items-center justify-content-center flex-grow-1 px-3'
        data-testid='amazon-status-badge'
      >
        {isProcessing ? (
          <>
            {t('Importing data')}
            <MiniLoader className='MiniLoader--sm d-flex align-items-center justify-content-center ms-2 ms-lg-3 MiniLoader--Notification MiniLoader--white' />
          </>
        ) : (
          <>{t('Amazon connected')}</>
        )}
      </div>
    </div>
  );

  const renderContent = () => {
    if (isDataImported) {
      return (
        <>
          <div className='d-none d-lg-block flex-grow-1'>
            <div className='Title fw-900 mb-2 AmazonConnection__mw400'>
              {t('Amazon is connected')}
            </div>
            <div className='Text'>
              {t('Your data is processed and fully imported')}
            </div>
            <div className='Text mb-4'>
              {t('Now you can check your VAT setup')}
            </div>
            <Button className='AmazonConnection__btn' onClick={onCheckNowClick}>
              {t('Show my setup')}
            </Button>
          </div>
          <div className='d-flex flex-column justify-content-between d-lg-none flex-grow-1'>
            <div className='d-flex flex-column align-items-center'>
              <img
                src={AmazonImportedImage}
                alt='AmazonImportedImage'
                className='AmazonConnection__image mx-auto'
              />
              <div className='mt-3 pt-3 mb-2 text-center'>
                {t('Step *n* of 3').replace('*n*', '3')}
              </div>
              <div className='Title fw-900 mb-2 AmazonConnection__mw400 text-center'>
                {t('Amazon is connected')}
              </div>
              <div className='Text mb-3 text-center'>
                {t('Your data is processed and fully imported')}&nbsp;
                {i18n.language === 'en' && <br />}
                {t('Now you can check your VAT setup')}
              </div>
              <Button
                className='AmazonConnection__btn w-auto mb-3'
                onClick={onCheckNowClick}
              >
                {t('Show my setup')}
              </Button>
            </div>
            <div className='pb-3 mb-1'>
              <div className='Text--20 fw-bold text-center'>
                {t('Imported')}
              </div>
              <div className='text-center mb-4'>
                {t('We also have sent your results to *email*').replace(
                  '*email*',
                  email || ''
                )}
              </div>
              <div className='d-flex justify-content-center'>
                <Progress
                  max={1}
                  value={1}
                  className='Progress--AmazonConnection'
                  getProgressBarColor={() => {
                    return '#FFE609';
                  }}
                />
              </div>
            </div>
          </div>
        </>
      );
    }

    return (
      <>
        {isProcessing && (
          <>
            <div className='d-lg-none d-flex flex-column flex-grow-1 justify-content-between'>
              <div>
                <img
                  src={AmazonConnectImage}
                  alt='AmazonConnectImage'
                  className='AmazonConnection__image mx-auto'
                />
                <div className='mt-3 pt-3 mb-2 text-center'>
                  {t('Step *n* of 3').replace('*n*', '2')}
                </div>
                <div className='Title fw-900 mb-2 text-center'>
                  {t('Connect Amazon')}
                </div>
                <div
                  className='Text mb-3 text-center'
                  style={{
                    maxWidth: 506,
                  }}
                >
                  {t(
                    'Login to your Amazon account and select your main seller center. Once your data is connected we will import your data'
                  )}
                </div>
                {[
                  'Your data is 100% protected',
                  'All data is anonymized',
                  'There is no access to your Seller account',
                  'Only relevant data is processed',
                ].map((p) => (
                  <div
                    className='Text mb-2 d-flex AmazonConnection__point mx-auto'
                    key={p}
                  >
                    <ShieldIcon className='me-2 flex-shrink-0' />
                    <div className='multiline-lg'>{t(p)}</div>
                  </div>
                ))}
                <div className='pt-2'>{connectedBadge}</div>
              </div>
              <div className='pb-3 mb-1'>
                <div
                  className='Text--20 fw-900 text-center mb-2'
                  data-testid='time'
                >
                  {isNaN(progressPercent) || progressPercent < 0.5 ? (
                    t('Starting processing')
                  ) : (
                    <>
                      {Math.round(progressPercent)}% {t('processed')}
                    </>
                  )}
                </div>
                <div className='text-center mb-4'>
                  {t(
                    'It might take a few minutes. We will notify to *email* once it is done'
                  ).replace('*email*', email || '')}
                </div>
                <div className='d-flex justify-content-center'>
                  <Progress
                    max={progressData?.total || 100}
                    value={
                      isNaN(progressPercent) || progressPercent < 0.5
                        ? 0
                        : progressData?.processed || 0
                    }
                    className='Progress--AmazonConnection'
                    getProgressBarColor={() => {
                      return '#FFE609';
                    }}
                  />
                </div>
              </div>
            </div>
          </>
        )}
        <div
          className={classNames({
            'd-none d-lg-block': isProcessing,
            'd-flex flex-column flex-grow-1 justify-content-between':
              !isProcessing,
          })}
        >
          <div className='AmazonConnection__content mx-auto mx-lg-0'>
            <div className='d-flex justify-content-center mb-3 pb-3 d-lg-none'>
              {isError ? (
                <img
                  src={AmazonConnectImageError}
                  alt='AmazonConnectImageError'
                  className='AmazonConnection__image mx-auto'
                />
              ) : (
                <img
                  src={AmazonConnectImage}
                  alt='AmazonConnectImage'
                  className='AmazonConnection__image mx-auto'
                />
              )}
            </div>
            <div className='d-lg-none mb-2 text-center'>
              {t('Step *n* of 3').replace('*n*', '1')}
            </div>
            <div className='Title fw-900 mb-2'>{t('Connect Amazon')}</div>
            <div
              className='Text mb-3'
              style={{
                maxWidth: 506,
              }}
            >
              {t(
                'Login to your Amazon account and select your main seller center. Once your data is connected we will import your data'
              )}
            </div>
            {[
              'Your data is 100% protected',
              'All data is anonymized',
              'There is no access to your Seller account',
              'Only relevant data is processed',
            ].map((p) => (
              <div
                className='Text mb-3 d-flex AmazonConnection__point mx-auto'
                key={p}
              >
                <ShieldIcon className='me-2 flex-shrink-0' />
                <div className='mt-n1 multiline-lg'>{t(p)}</div>
              </div>
            ))}
            <div className='pt-3'>
              {hasNewCredentials ? (
                <div className='AmazonConnection__mw506 text-center'>
                  {connectedBadge}
                </div>
              ) : (
                <div className='AmazonConnection__mw506 text-center'>
                  <a
                    className={classNames(
                      'Text AmazonConnection__authorizeBtn d-flex p-0 align-items-stretch text-decoration-none w-100 overflow-hidden mb-4',
                      {
                        'AmazonConnection__authorizeBtn--error': isError,
                      }
                    )}
                    href={`https://sellercentral-europe.amazon.com/apps/authorize/consent?application_id=${process.env.REACT_APP_ID}&state=${user?.Id}setup-check&version=beta`}
                  >
                    <div
                      className={`AmazonConnection__authorizeIcon d-flex align-items-center justify-content-center`}
                    >
                      <AmazonLogo />
                    </div>
                    <div className='d-flex align-items-center justify-content-center flex-grow-1 px-3'>
                      {t(
                        isError
                          ? 'Connection error. Try again'
                          : 'connect_amazon_btn'
                      )}
                    </div>
                  </a>
                  {vs2931NotAmazonSellerBtn &&
                    !isDataImported &&
                    !isProcessing && (
                      <Button
                        styling='naked'
                        className='Text mt-lg-2'
                        onClick={() => {
                          patchUser({
                            IsAmazonSeller: false,
                          }).catch(console.error);
                          setIsAmazonSeller(false);
                        }}
                      >
                        {t('I am not an Amazon Seller')}
                      </Button>
                    )}
                </div>
              )}
            </div>
          </div>
          <div className='d-lg-none'>
            <div className='text-center mt-3 pt-3 Text--20 fw-900 mb-2'>
              {t('Approx. 15 min')}
            </div>
            <div className='text-center mb-4'>
              {t('We will notify to *email* once it is done').replace(
                '*email*',
                email || ''
              )}
            </div>
          </div>
        </div>
      </>
    );
  };

  if (isLoading) return <Loader />;

  if (!isAmazonSeller && vs2931NotAmazonSellerBtn) {
    return (
      <AmazonConnectionConsultation
        onClickBtn={() => {
          setIsAmazonSeller(true);
        }}
      />
    );
  }

  return (
    <WhiteGreenWrapper
      data-testid='amazon-connection'
      rightSideContent={renderRightColumnContent()}
      className='WhiteGreenWrapper--AmazonConnection'
    >
      <div className='d-flex flex-column flex-grow-1 justify-content-between pb-4'>
        {renderContent()}
      </div>
    </WhiteGreenWrapper>
  );
}

const pollAmazonStatus = async (
  onFinish: () => void,
  onUpdate: ((data: AmazonDataImportStatusModel) => void) | undefined,
  controller: AbortController,
  count: number = 1
) => {
  try {
    const res = await getAmazonDataImportStatus(controller);

    if (onUpdate) onUpdate(res || undefined);

    if (res.status === 'loaded') {
      onFinish();
    } else {
      await sleep(1500);
      await pollAmazonStatus(onFinish, onUpdate, controller);
    }
  } catch {
    if (count < 3) {
      await sleep(1500);
      try {
        await pollAmazonStatus(onFinish, onUpdate, controller, ++count);
      } catch {}
    }
  }
};

const getProgressPercent = (total: number = 100, current: number = 0) => {
  return (current / total) * 100;
};
